import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DicomViewerComponent } from 'app/shared/components/dicom-viewer/dicom-viewer.component';
import { ModelViewerPageComponent } from '../model-viewer-page/model-viewer-page.component';
import { PatientsService } from 'app/core/services/patient/patients.service';
import { PermissionService } from 'app/core/services/user/permission.service';
import { PatientOverviewComponent } from '../patient/patient-detail/patient-overview/patient-overview.component';
import { PatientDetailResponse } from 'app/core/models/patient/patient-detail.model';
import { WoundService } from 'app/core/services/wound/wound.service';
import {
  GetListWoundByPatientIdResponse,
  GetWoundByIdResponse,
  WoundAnalysisMetrics,
} from 'app/core/models/wound/wound.model';
import { BaseApiResponse } from 'app/core/models/api/api.model';
import { WoundTimelineComponent } from 'app/shared/components/wound-timeline/wound-timeline.component';
import { ConclusionComponent } from '../conclusion/conclusion.component';
import { ConclusionService } from 'app/core/services/conclusion/conclusion.service';
import { ChatComponent } from '../chat/chat.component';
import { DocumentManagerComponent } from 'app/shared/components/document-manager/document-manager.component';
import { CurrentUserService } from 'app/core/services/auth/current-user.service';
import { WoundHubService } from 'app/core/services/wound/wound-hub.service';
import { WoundAnalysisMetricsComponent } from 'app/shared/components/wound-analysis-metrics/wound-analysis-metrics.component';
import { AiAssistantComponent } from '../ai-assistant/ai-assistant.component';

interface RadiologyImage {
  dicomUrl: string;
  date: string;
}

interface RadiologyDetails {
  modality: string;
  bodyPart: string;
  requestedBy: string;
  date: string;
  note?: string;
}

interface Consultation {
  date: string;
  status: 'completed' | 'active' | 'pending';
  radiologyData?: RadiologyDetails;
  images?: RadiologyImage[];
}

@Component({
  selector: 'app-worklist-detail',
  standalone: true,
  imports: [
    DicomViewerComponent,
    TranslateModule,
    CommonModule,
    ModelViewerPageComponent,
    PatientOverviewComponent,
    WoundTimelineComponent,
    ConclusionComponent,
    ChatComponent,
    DocumentManagerComponent,
    WoundAnalysisMetricsComponent,
    AiAssistantComponent,
  ],
  templateUrl: './worklist-detail.component.html',
  styleUrl: './worklist-detail.component.scss',
})
export class WorklistDetailComponent implements OnInit, OnDestroy {
  patientId!: string;
  woundId!: string;
  patient!: PatientDetailResponse;
  wounds: GetListWoundByPatientIdResponse[] = [];
  grid: 1 | 2 = 2;
  _activeTab: 'chat' | 'tahliller' | 'models' | 'patient' | 'conclusion' | 'metrics' | 'ai-assistant' =
    'models';
  selectedConsultationIndex: number = 4;
  isConclusionExit: boolean = false;
  canUploadDocuments: boolean = false;
  woundIds: string[] = [];
  woundDateMap: { [id: string]: Date } = {};
  showModelViewer: boolean = false;
  wound: GetWoundByIdResponse | null = null;
  parsedMetrics: WoundAnalysisMetrics | null = null;
  @ViewChild(ModelViewerPageComponent)
  modelViewerPage!: ModelViewerPageComponent;
  assignedDoctorIds: string[] = [];

  // Touch event handlers
  private boundTouchMove!: (event: TouchEvent) => void;
  private boundGestureStart!: (event: Event) => void;

  allConsultations: Consultation[] = [
    {
      date: '13-01-2024',
      status: 'completed',
      radiologyData: {
        modality: 'CT Scan',
        bodyPart: 'Chest',
        requestedBy: 'Cardiology',
        date: '13 Jan 2024',
        note: 'Normal cardiac silhouette. No pleural effusion.',
      },
      images: [
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000003.dcm',
          date: '2024-01-13',
        },
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000005.dcm',
          date: '2024-01-13',
        },
      ],
    },
    {
      date: '25-02-2024',
      status: 'completed',
      radiologyData: {
        modality: 'X-Ray',
        bodyPart: 'Lumbar Spine',
        requestedBy: 'Orthopedics',
        date: '25 Feb 2024',
        note: 'Mild degenerative changes. No fractures detected.',
      },
      images: [
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000002.dcm',
          date: '2024-02-25',
        },
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000011.dcm',
          date: '2024-02-25',
        },
      ],
    },
    {
      date: '15-05-2024',
      status: 'completed',
      radiologyData: {
        modality: 'Ultrasound',
        bodyPart: 'Abdomen',
        requestedBy: 'Gastroenterology',
        date: '15 May 2024',
        note: 'Liver and gallbladder within normal limits.',
      },
      images: [
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000005.dcm',
          date: '2024-05-15',
        },
      ],
    },
    {
      date: '08-08-2024',
      status: 'completed',
      radiologyData: {
        modality: 'MRI',
        bodyPart: 'Knee',
        requestedBy: 'Sports Medicine',
        date: '08 Aug 2024',
        note: 'Small meniscal tear. ACL intact.',
      },
      images: [
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000003.dcm',
          date: '2024-08-08',
        },
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000002.dcm',
          date: '2024-08-08',
        },
      ],
    },
    {
      date: '12-11-2025',
      status: 'active',
      radiologyData: {
        modality: 'MRI',
        bodyPart: 'Brain',
        requestedBy: 'Neurology',
        date: '12 Nov 2025',
        note: 'No acute intracranial pathology detected.',
      },
      images: [
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000011.dcm',
          date: '2025-11-12',
        },
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000002.dcm',
          date: '2025-11-12',
        },
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000003.dcm',
          date: '2025-11-12',
        },
        {
          dicomUrl: 'wadouri:/assets/dicom/image-000005.dcm',
          date: '2025-11-12',
        },
      ],
    },
    {
      date: '20-11-2025',
      status: 'pending',
      radiologyData: {
        modality: 'CT Scan',
        bodyPart: 'Sinus',
        requestedBy: 'ENT',
        date: '20 Nov 2025',
        note: 'Scheduled for evaluation.',
      },
      images: [],
    },
    {
      date: '05-12-2025',
      status: 'pending',
      radiologyData: {
        modality: 'X-Ray',
        bodyPart: 'Shoulder',
        requestedBy: 'Orthopedics',
        date: '05 Dec 2025',
        note: 'Awaiting scan.',
      },
      images: [],
    },
    {
      date: '15-12-2025',
      status: 'pending',
      radiologyData: {
        modality: 'MRI',
        bodyPart: 'Cervical Spine',
        requestedBy: 'Neurology',
        date: '15 Dec 2025',
        note: 'Scheduled appointment.',
      },
      images: [],
    },
    {
      date: '28-12-2025',
      status: 'pending',
      radiologyData: {
        modality: 'Ultrasound',
        bodyPart: 'Thyroid',
        requestedBy: 'Endocrinology',
        date: '28 Dec 2025',
        note: 'Routine checkup.',
      },
      images: [],
    },
    {
      date: '10-01-2026',
      status: 'pending',
      radiologyData: {
        modality: 'CT Scan',
        bodyPart: 'Pelvis',
        requestedBy: 'Urology',
        date: '10 Jan 2026',
        note: 'Follow-up required.',
      },
      images: [],
    },
    {
      date: '25-01-2026',
      status: 'pending',
      radiologyData: {
        modality: 'MRI',
        bodyPart: 'Hip',
        requestedBy: 'Orthopedics',
        date: '25 Jan 2026',
        note: 'Pre-surgical evaluation.',
      },
      images: [],
    },
    {
      date: '08-02-2026',
      status: 'pending',
      radiologyData: {
        modality: 'X-Ray',
        bodyPart: 'Chest',
        requestedBy: 'Pulmonology',
        date: '08 Feb 2026',
        note: 'Annual screening.',
      },
      images: [],
    },
  ];

  constructor(
    private activatedRoute: ActivatedRoute,
    private patientService: PatientsService,
    private woundService: WoundService,
    private conclusionService: ConclusionService,
    private permissionService: PermissionService,
    private currentUserService: CurrentUserService,
    private woundHubService: WoundHubService,
    private http: HttpClient,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadPatientData();
    this.checkConclusion();
    this.checkUserPermissions();
    this.setupTouchHandlers();
    this.showModelViewer = true;
    if (this.woundId) {
      this.woundHubService.startConnection(null);
      this.loadWoundDetail();
    }
  }

  private checkConclusion() {
    this.conclusionService
      .checkConclusion(this.patientId, this.woundId)
      .subscribe((res) => {
        this.isConclusionExit = res.data.isExist;
      });
  }

  private async loadPatientData(): Promise<void> {
    const patientId = this.activatedRoute.snapshot.paramMap.get('id');
    const woundId = this.activatedRoute.snapshot.paramMap.get('woundId');
    if (patientId) {
      this.patientId = patientId;
      this.woundId = woundId;
      this.patientService.getPatientById(patientId).subscribe((res) => {
        this.patient = res;
      });
      this.loadWoundsData();
    }
  }

  onTimelineWoundClick(woundId: string) {
    if (this.activeTab === 'models' && this.modelViewerPage) {
      this.modelViewerPage.selectWound(woundId);
    }
  }

  onWoundIdsCalculated(ids: string[]) {
    this.woundIds = [...ids];
  }

  private setupTouchHandlers(): void {
    this.boundTouchMove = this.onTouchMove.bind(this);
    this.boundGestureStart = this.onGestureStart.bind(this);

    document.addEventListener('touchmove', this.boundTouchMove, {
      passive: false,
    });
    document.addEventListener('gesturestart', this.boundGestureStart, {
      passive: false,
    });
  }

  private onTouchMove(event: TouchEvent): void {
    // Check if touch event is within the worklist-detail component
    if (
      event.target &&
      (event.target as HTMLElement).closest('app-worklist-detail')
    ) {
      // Prevent all touch movement (both pinch zoom and vertical scrolling)
      event.preventDefault();
    }
  }

  private onGestureStart(event: Event): void {
    // Prevent pinch zoom gesture
    event.preventDefault();
  }

  private loadWoundsData(): void {
    this.woundService.getListWoundByPatientId(this.patientId).subscribe({
      next: (data: BaseApiResponse<GetListWoundByPatientIdResponse[]>) => {
        const allWounds = data.data;

        // Map all wound dates for easy access
        allWounds.forEach((w) => {
          this.woundDateMap[w.id] = w.createdDateTime;
          w.followUps?.forEach((fw) => {
            this.woundDateMap[fw.id] = fw.createdDateTime;
          });
        });

        if (this.woundId) {
          this.wounds = this.filterWoundsForTimeline(allWounds, this.woundId);
        } else {
          this.wounds = allWounds;
        }

        this.wounds.forEach((w) => {
          if (w.followUps && w.followUps.length > 0) {
            w.followUps.forEach((fw) => {
              if (fw.woundStatus !== 4) {
                const related = allWounds.find((x) => x.id === fw.id);
                if (related?.doctorId) this.addDoctorId(related.doctorId);
              }
            });
          } else {
            if (w.woundStatus !== 4 && w.doctorId) {
              this.addDoctorId(w.doctorId);
            }
          }
        });

        console.log('Seçili Yara(lar) için Doktorlar:', this.assignedDoctorIds);
      },
      error: () => {
        this.wounds = [];
      },
    });
  }

  private addDoctorId(id: string): void {
    if (!this.assignedDoctorIds.includes(id)) {
      this.assignedDoctorIds.push(id);
    }
  }

  private filterWoundsForTimeline(
    wounds: GetListWoundByPatientIdResponse[],
    targetWoundId: string,
  ): GetListWoundByPatientIdResponse[] {
    return wounds.filter((wound) => {
      // Check if it's the root wound
      if (wound.id === targetWoundId) return true;

      // Check if it's in the follow-ups (children)
      if (wound.followUps?.some((fw) => fw.id === targetWoundId)) {
        return true;
      }

      return false;
    });
  }

  get currentConsultations(): Consultation[] {
    return this.allConsultations;
  }

  get currentRadiology(): RadiologyDetails {
    return (
      this.allConsultations[this.selectedConsultationIndex]?.radiologyData || {
        modality: '-',
        bodyPart: '-',
        requestedBy: '-',
        date: '-',
        note: '',
      }
    );
  }

  get visibleImages(): RadiologyImage[] {
    const images =
      this.allConsultations[this.selectedConsultationIndex]?.images || [];
    return images.slice(0, this.grid);
  }

  get timelineProgress(): number {
    const completedCount = this.allConsultations.filter(
      (c) => c.status === 'completed',
    ).length;
    return (completedCount / this.allConsultations.length) * 100;
  }

  setGrid(grid: 1 | 2): void {
    if (this.grid === grid) return;

    this.grid = grid;
    this.triggerResize();
  }

  selectConsultation(index: number): void {
    if (this.allConsultations[index].status === 'pending') {
      return;
    }

    this.selectedConsultationIndex = index;
    this.triggerResize();
  }

  private triggerResize(): void {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  trackByDicomUrl(index: number, item: RadiologyImage): string {
    return item.dicomUrl;
  }

  get activeTab() {
    return this._activeTab;
  }

  set activeTab(
    value: 'chat' | 'tahliller' | 'models' | 'patient' | 'conclusion' | 'metrics' | 'ai-assistant',
  ) {
    this._activeTab = value;

    if (value === 'patient') {
      this.triggerResize();
    }
  }

  private loadWoundDetail(): void {
    this.woundService.getWoundById(this.woundId).subscribe({
      next: (data: BaseApiResponse<GetWoundByIdResponse>) => {
        this.wound = data.data;
        if (this.wound?.metricsPath) {
          this._activeTab = 'metrics';
        }
        this.parseWoundMetrics();
      },
    });
  }

  private parseWoundMetrics(): void {
    if (this.wound?.metricsPath) {
      this.http.get<Record<string, unknown>>(this.wound.metricsPath).subscribe({
        next: (data) => this.applyMetrics(data),
        error: () => { this.parsedMetrics = null; },
      });
    }
  }

  private applyMetrics(raw: Record<string, unknown>): void {
    this.parsedMetrics = {
      surfaceAreaMm2: (raw['surface_area_mm2'] ?? raw['surfaceAreaMm2'] ?? 0) as number,
      surfaceAreaCm2: (raw['surface_area_cm2'] ?? raw['surfaceAreaCm2'] ?? 0) as number,
      volumeMm3: (raw['volume_mm3'] ?? raw['volumeMm3'] ?? 0) as number,
      volumeCm3: (raw['volume_cm3'] ?? raw['volumeCm3'] ?? 0) as number,
      volumeIsApproximate: (raw['volume_is_approximate'] ?? raw['volumeIsApproximate'] ?? false) as boolean,
      isWatertight: (raw['is_watertight'] ?? raw['isWatertight'] ?? false) as boolean,
      boundingBoxMm: (raw['bounding_box_mm'] ?? raw['boundingBoxMm']) as number[] | undefined,
      centroidMm: (raw['centroid_mm'] ?? raw['centroidMm']) as number[] | undefined,
      boundaryLengthMm: (raw['boundary_length_mm'] ?? raw['boundaryLengthMm'] ?? 0) as number,
      numVertices: (raw['num_vertices'] ?? raw['numVertices'] ?? 0) as number,
      numFaces: (raw['num_faces'] ?? raw['numFaces'] ?? 0) as number,
      steepestViewOriginalPath: (raw['steepest_view_original'] ?? raw['steepestViewOriginalPath'] ?? null) as string | null,
      steepestViewSegmentedPath: (raw['steepest_view_segmented'] ?? raw['steepestViewSegmentedPath'] ?? null) as string | null,
    };
  }

  get shouldDisableConclusionButton(): boolean {
    const hasNurseClaim = this.permissionService.hasPermission('nurse_claim');
    if (hasNurseClaim && !this.isConclusionExit) {
      return true;
    }
    return false;
  }

  private checkUserPermissions(): void {
    const session = this.currentUserService.GetSession();
    if (session && session.role) {
      const roleName = session.role.name?.toLowerCase();
      // Allow upload for nurse role
      this.canUploadDocuments = roleName === 'nurse' || roleName === 'hemşire';
    }
  }

  ngOnDestroy(): void {
    // Clean up touch event listeners
    if (this.boundTouchMove) {
      document.removeEventListener('touchmove', this.boundTouchMove);
    }
    if (this.boundGestureStart) {
      document.removeEventListener('gesturestart', this.boundGestureStart);
    }
  }


}
